import { convexQuery } from "@convex-dev/react-query";
import { api } from "@convex/_generated/api";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Navigate,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";

import { organizationsGetActive } from "../api-refs";
import { authClient } from "../integrations/vortex-auth/client";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
const getBusinessInformationRef = api.organizations.getBusinessInformation;

const getEmployeeByUserIdRef = api.employees.getEmployeeByUserId;

import { PlasmaAIWithLayout } from "../components/ai/plasma-ai";
import { AppSidebar } from "../components/app-sidebar";
import { CommandPalette } from "../components/command-palette";
import { EmployeePermissionsProvider } from "../components/providers/employee-permissions-provider";
import { SidebarProvider } from "../components/ui/sidebar";
import { Spinner } from "../components/ui/spinner";

export const Route = createFileRoute("/_authenticated")({
  // Type-safe auth guard: runs before component renders.
  // Sourced directly from the vortex-auth client so this route does not depend
  // on whichever auth provider is currently wired into the router context.
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data) {
      throw redirect({
        to: "/sign-in",
        search: {
          // Preserve the intended destination for post-login redirect.
          redirect: location.pathname,
        },
      });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isLoading: isConvexLoading, isAuthenticated } = useConvexAuth();

  // Active organization is sourced from plasma's `users.activeVortexOrgId`
  // via the `organizations.getActive` query (G2 finding: vortex-auth's JWT
  // does not carry active org context).
  const { data: organization, isLoading: isOrgLoading } = useQuery({
    ...convexQuery(organizationsGetActive, isAuthenticated ? {} : "skip"),
  });

  // Check if Stripe account exists (not full onboarding - just account creation)
  const { data: businessData, isLoading: isBusinessLoading } = useQuery({
    ...convexQuery(
      getBusinessInformationRef,
      isAuthenticated && organization ? {} : "skip"
    ),
  });

  // Check if the CURRENT USER has an employee profile
  // Uses getEmployeeByUserId which doesn't require permissions - it only looks up
  // the current user's own employee record by their userId
  const { data: currentEmployeeData, isLoading: isEmployeeLoading } = useQuery({
    ...convexQuery(
      getEmployeeByUserIdRef,
      isAuthenticated && organization ? {} : "skip"
    ),
  });

  // Requirements: User + Active Org + Stripe Account + Employee Profile = Portal Access
  // Note: Full onboarding not required - ConnectNotificationBanner will prompt when needed
  const isLoading =
    isConvexLoading ||
    (isAuthenticated && isOrgLoading) ||
    Boolean(organization && isBusinessLoading) ||
    Boolean(organization && isEmployeeLoading);
  const hasStripeAccount = !!businessData?.stripeAccountId;
  const hasEmployeeProfile = !!currentEmployeeData?.employee;

  // Auth is guaranteed by beforeLoad guard - only need to handle org/business checks
  if (isLoading) {
    return (
      <div className="flex h-svh items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (!organization) {
    // No active organization - redirect to onboarding
    return <Navigate search={{ setup: undefined }} to="/onboarding" />;
  }

  if (!hasStripeAccount) {
    // Has active org but no Stripe account - redirect to onboarding to create one
    return <Navigate search={{ setup: undefined }} to="/onboarding" />;
  }

  if (!hasEmployeeProfile) {
    // User doesn't have an employee profile - redirect to setup to create one
    return <Navigate to="/setup" />;
  }

  // Has User + Active Org + Stripe Account + Employee Profile - show the portal
  // ConnectNotificationBanner will prompt for remaining requirements
  return (
    <EmployeePermissionsProvider>
      <CommandPalette />
      <SidebarProvider>
        <AppSidebar />
        <PlasmaAIWithLayout>
          <div className="h-svh w-full overflow-hidden lg:p-2">
            <div className="bg-container flex h-full w-full flex-col overflow-hidden lg:rounded-md lg:border">
              <main className="w-full flex-1 scroll-pb-24 overflow-auto">
                <Outlet />
              </main>
            </div>
          </div>
        </PlasmaAIWithLayout>
      </SidebarProvider>
    </EmployeePermissionsProvider>
  );
}
